import { ResumeStatus } from "@/_models/resume";
import type {
  BenchmarkRankingResponse,
  ResumeBenchmarkDetailResponse,
  ResumeHistoryListResponse,
} from "@/_models/resume-benchmark";
import { mainApiInstance } from "../_axiosInstance";
import { GenerateATSResponse } from "./ats-resume";

interface InitResumeBenchmarkRequest {
  files: {
    fileName: string;
    fileType: string;
  }[];
  fingerprintId: string;
}

interface InitResumeBenchmarkResponse {
  s3_presigned_url_origin: string;
  s3_presigned_url_thumbnail: string;
  gcloud_presigned_url: string;
  resume_id: string;
}

const initResumeBenchmark = async (request: InitResumeBenchmarkRequest) => {
  try {
    const response = await mainApiInstance.post<InitResumeBenchmarkResponse>(
      "/resume-benchmark/init",
      request,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

interface ResumeBenchmarkStatusResponse {
  id: string;
  status: ResumeStatus | string;
}

interface StartResumeBenchmarkResponse {
  resume_id: string;
}

interface StartResumeBenchmarkRequest {
  resume_id: string;
  output_language: "English" | "Vietnamese";
}
const getResumeBenchmarkStatus = async (resumeId: string) => {
  try {
    const response = await mainApiInstance.get<ResumeBenchmarkStatusResponse>(
      `/resume-benchmark/status`,
      {
        params: {
          resumeId,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const startResumeBenchmark = async (
  fingerprintId: string,
  originalFile: File,
  thumbnailFile: File,
  outputLanguage: "en" | "vi",
) => {
  try {
    const payload = {
      files: [
        {
          fileName: originalFile.name,
          fileType: originalFile.type,
        },
      ],
      fingerprintId,
    };
    const responseData = await initResumeBenchmark(payload);
    await Promise.all([
      fetch(responseData.s3_presigned_url_origin, {
        method: "PUT",
        body: originalFile,
      }),
      fetch(responseData.s3_presigned_url_thumbnail, {
        method: "PUT",
        body: thumbnailFile,
      }),
      fetch(responseData.gcloud_presigned_url, {
        method: "PUT",
        body: originalFile,
      }),
    ]);
    const response = await mainApiInstance.post<StartResumeBenchmarkResponse>(
      `/resume-benchmark/start`,
      {
        resume_id: responseData.resume_id,
        output_language: outputLanguage === "vi" ? "Vietnamese" : "English",
      } satisfies StartResumeBenchmarkRequest,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getResumeBenchmarkDetails = async (
  resumeId: string,
  fingerprintId: string,
) => {
  try {
    const response = await mainApiInstance.get<ResumeBenchmarkDetailResponse>(
      `/resume-benchmark/detail`,
      {
        params: {
          resumeId,
          fingerprintId,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

interface GetResumeHistoryListParams {
  page?: number;
  limit?: number;
}

const getResumeHistoryList = async (
  params: GetResumeHistoryListParams = {},
): Promise<ResumeHistoryListResponse> => {
  try {
    const response = await mainApiInstance.get<ResumeHistoryListResponse>(
      `/resume-benchmark/history`,
      {
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getResumeHistoryDetail = async (resumeId: string) => {
  try {
    const response = await mainApiInstance.get<ResumeBenchmarkDetailResponse>(
      `/resume-benchmark/history/${resumeId}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getHistoryByResumeId = async (resumeId: string) => {
  try {
    const response = await mainApiInstance.get<ResumeBenchmarkDetailResponse>(
      `/resume-benchmark/history/resume/${resumeId}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const deleteResumeHistory = async (resumeId: string) => {
  try {
    const response = await mainApiInstance.delete<void>(
      `/resume-benchmark/history/${resumeId}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const generateATSFromHistory = async (historyId: string) => {
  try {
    const response = await mainApiInstance.post<GenerateATSResponse>(
      `/resume-benchmark/history/${historyId}/ats-generate`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getBenchmarkRanking = async (resumeId: string) => {
  try {
    const response = await mainApiInstance.get<BenchmarkRankingResponse>(
      `/resume-benchmark/${resumeId}/ranking`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resumeBenchmarkService = {
  initResumeBenchmark,
  getResumeBenchmarkStatus,
  startResumeBenchmark,
  getResumeHistoryList,
  getResumeHistoryDetail,
  deleteResumeHistory,
  generateATSFromHistory,
  getBenchmarkRanking,
  getHistoryByResumeId,
};
